import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { isNil } from 'lodash-es';
import {
  EMPTY,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { HasPermissionDirective, PermissionService, UserService as ToolsetUserService } from '@ipg/ngx-frontend-toolset';
import { User } from '../../models/user.model';
import { TableLimitService } from '../../services/table-limit.service';
import { escapeHtml } from '../../utils/html';
import {
  UserCredentialsDialogComponent,
  UserCredentialsDialogData,
} from './components/user-credentials-dialog/user-credentials-dialog.component';
import { UserService } from './services/user.service';

@Component({
  selector: 'app-users',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTableModule,
    MatTooltip,
    MatPaginator,
    DatePipe,
    DataTableContainerComponent,
    MatFormField,
    FormsModule,
    MatInput,
    MatSort,
    MatSortHeader,
    MatSuffix,
    HasPermissionDirective,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersComponent {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _userService = inject(UserService);
  private readonly _dialogService = inject(DialogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _toolsetUserService = inject(ToolsetUserService);
  private readonly _fetch$ = new Subject<void>();

  /** Current authenticated user's ID */
  protected readonly currentUserId = computed(() => {
    const user = this._toolsetUserService.data() as Record<string, unknown> | null;
    return (user?.['_id'] as string) ?? null;
  });

  protected isSelf(element: User): boolean {
    return element._id === this.currentUserId();
  }

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);
  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'display_name',
    sortOrder: 'asc',
  });
  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(
    map(({ query, sort, keyword }) => ({
      ...query,
      ...sort,
      ...(keyword ? { q: keyword.trim() } : null),
    })),
    debounceTime(0)
  );

  private readonly _hasLoadedData = signal<boolean>(false);

  private readonly _paginatedUsers$ = this._params$.pipe(
    switchMap((params) =>
      this._userService.getList({
        params,
      })
    ),
    tap(() => this._hasLoadedData.set(true))
  );

  private readonly _users$ = this._paginatedUsers$.pipe(map(({ items }) => items));

  protected readonly dataSource = toSignal(this._users$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly isSearchDisabled = computed(() => !this._hasLoadedData());

  displayedColumns = ['idAlias', 'displayName', 'role', 'createdAt', 'actions'];

  private showCredentialsDialog(data: UserCredentialsDialogData) {
    return this._dialogService
      .open<UserCredentialsDialogComponent, UserCredentialsDialogData>(
        UserCredentialsDialogComponent,
        {
          ...data,
        },
        {
          disableClose: true,
        }
      )
      .afterClosed();
  }

  protected handleCreateUser(): void {
    this._router.navigate(['new'], { relativeTo: this._route });
  }

  protected handleEditUser(user: User): void {
    if (!this._permissionService.hasPermission('user:update')) return;
    this._router.navigate([user._id], { relativeTo: this._route });
  }

  protected handleResetPassword(user: User): void {
    this._dialogService
      .confirm({
        title: 'Wachtwoord resetten',
        message: `Weet je zeker dat je het wachtwoord van <strong>${escapeHtml(user.displayName)}</strong> wilt resetten?`,
        confirmText: 'Resetten',
        cancelText: 'Annuleren',
        onConfirm: () =>
          this._userService.resetPassword(user._id).pipe(
            catchError(() => {
              this._dialogService.alert({
                title: 'Foutmelding',
                message: `Er is iets misgegaan bij het resetten het wachtwoord voor gebruiker ${escapeHtml(user.displayName)}. Probeer het later nog eens`,
                confirmText: 'OK',
              });

              return EMPTY;
            })
          ),
      })
      .afterClosed()
      .pipe(filter((response) => !isNil(response)))
      .subscribe({
        next: (response: UserCredentialsDialogData) => {
          this.showCredentialsDialog({
            title: 'Wachtwoord gereset',
            message: 'Het wachtwoord is succesvol gereset.',
            accountId: response.accountId,
            temporaryPassword: response.temporaryPassword,
          });
        },
      });
  }

  protected handleDeleteUser(user: User): void {
    // Prevent self-deletion
    if (user._id === this.currentUserId()) {
      this._dialogService.alert({
        title: 'Actie niet toegestaan',
        message: 'Je kunt je eigen account niet verwijderen.',
        confirmText: 'OK',
      });
      return;
    }

    this._dialogService.confirm({
      title: 'Gebruiker verwijderen',
      message:
        `Weet je zeker dat je de gebruiker <strong>${escapeHtml(user.displayName)}</strong> wilt verwijderen? ` +
        `De gebruiker verliest alle toegang tot de applicatie. ` +
        `Deze actie kan niet ongedaan worden gemaakt.`,
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () => {
        return this._userService.delete(user._id).pipe(
          catchError(() => {
            this._dialogService.alert({
              title: 'Foutmelding',
              message: `Er is iets misgegaan bij het verwijderen van gebruiker ${escapeHtml(user.displayName)}. Probeer het later nog eens`,
              confirmText: 'OK',
            });

            return EMPTY;
          }),
          tap(() => this._fetch$.next())
        );
      },
    });
  }

  protected handleMoveUser(user: User): void {
    this._router.navigate([user._id, 'move'], { relativeTo: this._route });
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }
}
